import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/common/Navbar';
import RoundBackButton from '../../components/common/RoundBackButton';
import { COLORS, FONTS } from '../../styles/tokens';

export default function OrgSetupPage() {
  const { userDoc, refreshUserDoc } = useAuth();
  const navigate = useNavigate();
  const [loading,     setLoading]    = useState(false);
  const [error,       setError]      = useState('');
  const [checking,    setChecking]   = useState(true);
  const [existingOrg, setExistingOrg] = useState(null);

  // On mount: check if an org with the user's orgNumber already exists (spec section 3c / 4)
  useEffect(() => {
    if (!userDoc?.orgNumber) { setChecking(false); return; }
    (async () => {
      try {
        const snap = await getDocs(
          query(collection(db, 'organizations'), where('orgNumber', '==', userDoc.orgNumber))
        );
        // Transportør-orger (type 'carrier') deler org.nr., men er ikke et team man kan bli med i
        const found = snap.docs.find(d => d.data().type !== 'carrier');
        setExistingOrg(found ? { id: found.id, ...found.data() } : null);
      } catch (e) {
        // Permission denied or network error — treat as no existing org
        setExistingOrg(null);
      } finally {
        setChecking(false);
      }
    })();
  }, [userDoc?.orgNumber]);

  async function handleCreate() {
    setLoading(true);
    setError('');
    try {
      const fns = getFunctions(undefined, 'europe-west1');
      await httpsCallable(fns, 'createOrg')({});
      await refreshUserDoc();
      navigate('/orderer/org');
    } catch (err) {
      setError(err.message ?? 'Kunne ikke opprette team. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  async function handleJoinRequest() {
    setLoading(true);
    setError('');
    try {
      const fns = getFunctions(undefined, 'europe-west1');
      await httpsCallable(fns, 'requestToJoinOrg')({});
      navigate('/orderer');
    } catch (err) {
      setError(err.message ?? 'Forespørsel feilet. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return (
      <div className="orderer-ui" style={styles.root}>
        <Navbar />
        <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" /><p style={styles.hint}>Sjekker organisasjon…</p></div>
      </div>
    );
  }

  return (
    <div className="orderer-ui" style={styles.root}>
      <Navbar />
      <div style={styles.page}>
          <RoundBackButton onClick={() => navigate('/orderer/profil')} label="Tilbake til profil" />
        <div style={styles.card}>
          <div style={styles.iconWrap}>🏢</div>

          {existingOrg ? (
            <>
              <h2 style={styles.title}>Bli med i {existingOrg.orgName}</h2>
              <p style={styles.intro}>
                Bedriften din er allerede registrert på Tippi. Send en forespørsel — en administrator godkjenner deg.
              </p>
              <div style={styles.field}>
                <label style={styles.label}>Org.nummer</label>
                <div style={styles.readOnly}>{userDoc?.orgNumber ?? '—'}</div>
              </div>
              {error && <div style={styles.error}>{error}</div>}
              <button className="tippi-btn" style={styles.btn} onClick={handleJoinRequest} disabled={loading}>
                {loading ? 'Sender…' : 'Send forespørsel'}
              </button>
              <button className="tippi-btn-secondary" style={styles.btnSecondary} onClick={() => navigate('/orderer')} disabled={loading}>
                Avbryt
              </button>
            </>
          ) : (
            <>
              <h2 style={styles.title}>Sett opp team</h2>
              <p style={styles.intro}>
                Opprett en organisasjon for bedriften din. Du blir automatisk administrator og kan invitere kolleger.
              </p>
              <div style={styles.field}>
                <label style={styles.label}>Organisasjonsnavn</label>
                <div style={styles.readOnly}>{userDoc?.orgName ?? '—'}</div>
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Org.nummer</label>
                <div style={styles.readOnly}>{userDoc?.orgNumber ?? '—'}</div>
              </div>
              <p style={styles.hint}>Disse opplysningene er hentet fra Enhetsregisteret og kan ikke endres her.</p>
              {error && <div style={styles.error}>{error}</div>}
              <button className="tippi-btn" style={styles.btn} onClick={handleCreate} disabled={loading}>
                {loading ? 'Oppretter…' : 'Opprett team'}
              </button>
              <button className="tippi-btn-secondary" style={styles.btnSecondary} onClick={() => navigate('/orderer')} disabled={loading}>
                Avbryt
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { minHeight: '100vh', background: COLORS.bg },
  page: { maxWidth: 480, margin: '0 auto', padding: '24px 20px', paddingTop: 'max(28px, env(safe-area-inset-top))' },
  card: {
    background: COLORS.white,
    borderRadius: 22,
    padding: 24,
    border: `1px solid ${COLORS.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  iconWrap: { fontSize: 40, textAlign: 'center' },
  title:   { fontFamily: FONTS.display, fontSize: 36, lineHeight: 1.05, fontWeight: 700, margin: 0, textAlign: 'center', color: COLORS.textDark },
  intro:   { fontSize: 14, color: COLORS.textLight, lineHeight: 1.5, margin: 0 },
  field:   { display: 'flex', flexDirection: 'column', gap: 4 },
  label:   { fontSize: 12, fontWeight: 600, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' },
  readOnly: {
    background: COLORS.bg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 14,
    padding: '14px',
    fontSize: 15.5,
    color: COLORS.textDark,
  },
  hint:         { fontSize: 12, color: COLORS.textLight, margin: 0 },
  error:        { background: COLORS.dangerBg, border: `1px solid ${COLORS.dangerBorder}`, borderRadius: 8, padding: '10px 14px', fontSize: 14, color: COLORS.dangerText },
  btn:          { width: '100%', height: 52, fontSize: 16, marginTop: 4, borderRadius: 26, border: 'none', background: COLORS.accent, color: '#1a1a2e', fontWeight: 700, cursor: 'pointer' },
  btnSecondary: { width: '100%', height: 52, fontSize: 15, marginTop: 0, borderRadius: 26, border: `1.5px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontWeight: 700, cursor: 'pointer' },
};
